import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HeaderService } from '../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { BaseComponent } from '../../core/base.component';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NotificationService, NotificationType } from '../../notification/notification.service';
import { LookupModel } from '../../api/models/lookup-model';
import { AuthService } from '../../core/security/auth.service';
import { RolesEnum } from '../../core/security/roles.enum';
import { PhoneNumbersService } from '../phone-numbers.service';
import { cnamValidator } from '../../core/validators/cnam.validator';
import { SavePhoneNumbersRequest } from '../../api/models/save-phone-numbers-request';
import { PhoneNumberModel } from '../../api/models/phone-number-model';
import { MessagingUserModel } from '../../api/models/messaging-user-model';
import { uniqueBy } from '../../core/validators/unique-by.validator';
import { FaxUserModel } from '../../api/models/fax-user-model';
import { CurrentUser } from '../../api/models/current-user';
import { MatAlertHtmlDialogComponent } from '../../dialogs/mat-alert-html/mat-alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FaxDomainModel } from '../../api/models/fax-domain-model';
import { ZendeskService } from '../../core/services/zendesk.service';
import { NgIf, NgFor } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCard, MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import { MessagesModule } from 'primeng/messages';
import { MatFormFieldFix } from '../../editors/mat-form-field/mat-form-field-fix';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatCheckboxInputComponent } from '../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatSimpleSelectComponent } from '../../editors/mat-simple-select/mat-simple-select.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { FlexModule } from '@angular/flex-layout';
import { MatTooltip } from '@angular/material/tooltip';
import { EmailListInputComponent } from '../../editors/email-list-input/email-list-input.component';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { PhoneNumberPipe } from '../../core/pipes/phone-number.pipe';
import { Message } from 'primeng/message';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-phone-number-edit',
  templateUrl: './phone-number-edit.component.html',
  styleUrls: ['./phone-number-edit.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    MatCard,
    MatCardTitle,
    MatCardContent,
    MatTextInputComponent,
    MessagesModule,
    MatFormFieldFix,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatCheckboxInputComponent,
    NgFor,
    MatSimpleSelectComponent,
    MatIconButton,
    MatIcon,
    FlexModule,
    MatButton,
    MatTooltip,
    EmailListInputComponent,
    Angular2PromiseButtonModule,
    PhoneNumberPipe,
    Message,
  ],
})
export class PhoneNumberEditComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  form: UntypedFormGroup;
  smsOptionControl = new UntypedFormControl('disabled', []);
  customerDidAliasControl = new UntypedFormControl('', []);
  cnamControl = new UntypedFormControl('', cnamValidator);
  smsMarkAllAsReadControl = new UntypedFormControl(false, []);

  submitted: boolean;
  campaignLimitExceeded: boolean;
  serverError: boolean;
  serverErrorMessage: string;
  editModel: PhoneNumberModel = { id: 'new', number: '' };
  customerUsers: LookupModel[] = [];
  customerFaxUsers: LookupModel[] = [];
  customerFaxDomains: LookupModel[] = [];
  buttonSubmitPromise: Promise<void>;
  cnamChanged = false;
  smsEnabledChanged = false;

  hasBasicMessaging = false;
  hasProMessaging = false;

  smsMessagingType = 'disabled';

  isLoading = true;

  private isInitialSmsEnabled = false;
  private paramsSubscription;
  private id: string;

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    public authService: AuthService,
    public dialog: MatDialog,
    private phoneNumbersService: PhoneNumbersService,
    private fb: UntypedFormBuilder,
    public zendeskService: ZendeskService,
    private notificationService: NotificationService,
  ) {
    super(route, headerService, titleService, authService);

    this.paramsSubscription = this.route.params.subscribe(params => {
      this.id = params['id'];
      console.log(this.id);
    });

    this.editRoles = [RolesEnum.PhoneNumbers_Edit, RolesEnum.PhoneNumbers_Delete];
    this.deleteRoles = [RolesEnum.PhoneNumbers_Delete];

    this.headerService.showBackButton(true);
    this.createForm();
  }

  get messagingUsers(): UntypedFormArray {
    return this.form.get('messagingUsers') as UntypedFormArray;
  }

  get faxUsers(): UntypedFormArray {
    return this.form.get('faxUsers') as UntypedFormArray;
  }

  get faxDomains(): UntypedFormArray {
    return this.form.get('faxDomains') as UntypedFormArray;
  }

  onCurrentUserChanged(user: CurrentUser) {
    console.log('onCurrentUserChanged');
    if (user) {
      this.hasBasicMessaging = user.modules.indexOf('MessagingController') > -1;
      this.hasProMessaging = user.modules.indexOf('ProMessagingController') > -1;
    } else {
      this.hasBasicMessaging = false;
      this.hasProMessaging = false;
    }
  }

  createForm(): void {
    this.form = this.fb.group({
      cnam: this.cnamControl,
      customerDidAlias: this.customerDidAliasControl,
      smsOption: this.smsOptionControl,
      smsMarkAllAsRead: this.smsMarkAllAsReadControl,
      messagingUsers: this.fb.array([], uniqueBy('customerUserId')),
      faxUsers: this.fb.array([], uniqueBy('customerUserId')),
      faxDomains: this.fb.array([], uniqueBy('domain')),
      allEmailGroups: this.fb.array([], uniqueBy('email')),
    });
  }

  async onSubmit() {
    this.serverError = false;
    this.campaignLimitExceeded = false;
    this.serverErrorMessage = '';
    this.form.markAllAsTouched();
    if (this.form.valid && this.hasEditAccess) {
      this.cnamChanged = this.form.get('cnam').dirty;
      this.buttonSubmitPromise = this.processSubmit();
    }
  }

  async processSubmit(): Promise<void> {
    try {
      console.log('processSubmit');
      this.submitted = true;
      const reqData = this.prepareFormModel();
      if (await this.phoneNumbersService.savePhoneNumbers(reqData)) {
        this.notificationService.notifySuccess('The changes have been submitted successfully');

        this.smsEnabledChanged = !this.isInitialSmsEnabled && reqData.smsEnabled;
        // this.ngOnChanges();
        if (this.cnamChanged || this.smsEnabledChanged) {
          let alertContent = '';
          if (this.cnamChanged) {
            alertContent += `
            <div class="alert alert-warning">
              Caller ID should be active within 7 to 14 business days.
              If the accurate Caller ID isn't showing 14 days after the request was submitted, contact support@telego.net for
              assistance.
            </div>
            `;
          }
          if (this.smsEnabledChanged) {
            alertContent += `
            <div class="alert alert-warning">
              Please note that processing could take up to 12 hours before SMS is fully functional.
            </div>
            `;
          }

          const dialogRef = this.dialog.open(MatAlertHtmlDialogComponent, {
            width: '400px',
            height: '300px',
            data: {
              title: 'Please Note',
              actionTitle: 'Close',
              html: alertContent,
            },
          });

          dialogRef.afterClosed().subscribe(() => {
            this.refreshPhoneNumberDetails(false);
          });
        } else {
          this.refreshPhoneNumberDetails(false);
        }
      } else {
        this.serverError = true;
      }
    } catch (e: any) {
      console.error('unable to save phone number', e);
      if (e instanceof HttpErrorResponse && e.error) {
        const errorResponse = JSON.parse(e.error);
        if (errorResponse && errorResponse.message == 'Campaign Capacity Exceeded') {
          this.campaignLimitExceeded = true;
        } else if (errorResponse && errorResponse.message) {
          this.serverErrorMessage = errorResponse.message;
          this.serverError = true;
        } else {
          this.serverErrorMessage = 'Unable to save phone number';
          this.serverError = true;
        }
      } else {
        this.serverError = true;
      }
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): SavePhoneNumbersRequest {
    const formModel = this.form.value;

    // deep copy of form model lairs
    const messagingUsersCopy: MessagingUserModel[] = formModel.messagingUsers.map((user: MessagingUserModel) =>
      Object.assign({}, user),
    );

    if (this.smsMessagingType !== 'pro' && this.smsMessagingType !== 'broadcasting' /*&& !this.editModel.allowWaba*/) {
      messagingUsersCopy.splice(1);
    }

    const faxUsersCopy: FaxUserModel[] = formModel.faxUsers.map((user: FaxUserModel) => Object.assign({}, user));

    const faxDomainsCopy: FaxDomainModel[] = formModel.faxDomains.map((item: FaxDomainModel) =>
      Object.assign({}, item),
    );

    const allEmails: any[] = formModel.allEmailGroups ? formModel.allEmailGroups : [];

    const faxInboundEmailsCopy: string[] = allEmails.filter(x => x.allowReceive).map(x => x.email);
    const faxOutboundEmailsCopy: string[] = allEmails.filter(x => x.allowSend).map(x => x.email);

    return {
      ids: [this.editModel.id],
      cnam: formModel.cnam,
      customerDidAlias: formModel.customerDidAlias,
      smsEnabled: formModel.smsOption !== 'disabled',
      smsProEnabled: formModel.smsOption === 'pro' || formModel.smsOption === 'broadcasting',
      broadcastEnabled: formModel.smsOption === 'pro' || formModel.smsOption === 'broadcasting',
      smsMarkAllAsRead: formModel.smsMarkAllAsRead,
      updateMessaging: true,
      updateFax: true,
      messagingUsers: messagingUsersCopy,
      faxUsers: faxUsersCopy,
      faxDomains: faxDomainsCopy,
      faxSettings: {
        username: this.editModel.faxSettings.username,
        displayName: this.editModel.faxSettings.displayName,
        password: this.editModel.faxSettings.password,
        phoneNumberId: this.editModel.id,
        id: this.editModel.faxSettings.id,
        inboundEmails: faxInboundEmailsCopy,
        outboundEmails: faxOutboundEmailsCopy,
      },
    };
  }

  setMessagingUsers(users: MessagingUserModel[]) {
    const usersFormGroups = users.map(user =>
      this.fb.group({
        id: [user.id],
        customerUserId: [user.customerUserId, Validators.required],
        phoneNumberId: [user.phoneNumberId],
        receiveEmail: [user.receiveEmail],
        includeText: [user.includeText],
      }),
    );
    const addressFormArray = this.fb.array(usersFormGroups, uniqueBy('customerUserId'));
    this.form.setControl('messagingUsers', addressFormArray);
  }

  removeMessagingUser(index) {
    this.messagingUsers.removeAt(index);
  }

  addMessagingUser() {
    this.messagingUsers.push(
      this.fb.group({
        id: '',
        customerUserId: ['', Validators.required],
        phoneNumberId: [this.editModel.id],
        receiveEmail: [true],
        includeText: [false],
      }),
    );
  }

  addAllMessagingUsers() {
    const formModel = this.form.value;
    const existingUsers: string[] = formModel.messagingUsers.map((user: MessagingUserModel) => user.customerUserId);

    const notIncludedUsers = this.customerUsers.filter(value => {
      return existingUsers.indexOf(value.id.toString()) === -1;
    });

    for (const user of notIncludedUsers) {
      this.messagingUsers.push(
        this.fb.group({
          id: '',
          customerUserId: [user.id, Validators.required],
          phoneNumberId: [this.editModel.id],
          receiveEmail: [true],
          includeText: [false],
        }),
      );
    }
  }

  setFaxUsers(users: FaxUserModel[]) {
    const usersFormGroups = users.map(user =>
      this.fb.group({
        id: [user.id],
        customerUserId: [user.customerUserId, Validators.required],
        phoneNumberId: [user.phoneNumberId],
        allowSend: [user.allowSend],
        allowReceive: [user.allowReceive],
        viewAllSend: [user.viewAllSend],
        receiveEmail: [user.receiveEmail],
      }),
    );
    const addressFormArray = this.fb.array(usersFormGroups, uniqueBy('customerUserId'));
    this.form.setControl('faxUsers', addressFormArray);
  }

  setInboundOutboundEmails(inboundEmails: string[], outboundEmails: string[]) {
    const emailObjList = inboundEmails.map(email => {
      return {
        email: email,
        allowReceive: true,
        allowSend: false,
        viewAllSend: false,
      };
    });

    for (const email of outboundEmails) {
      const emailObj = emailObjList.find(x => x.email === email);
      if (emailObj) {
        emailObj.allowSend = true;
      } else {
        emailObjList.push({
          email: email,
          allowReceive: false,
          allowSend: true,
          viewAllSend: false,
        });
      }
    }

    const allEmailGroups = emailObjList.map(emailObj =>
      this.fb.group({
        email: [emailObj.email, Validators.compose([Validators.email, Validators.required])],
        allowReceive: [emailObj.allowReceive],
        allowSend: [emailObj.allowSend],
        viewAllSend: [emailObj.viewAllSend],
      }),
    );
    const addressFormArray = this.fb.array(allEmailGroups, uniqueBy('email'));
    this.form.setControl('allEmailGroups', addressFormArray);
  }

  removeFaxUser(index) {
    this.faxUsers.removeAt(index);
  }

  addFaxUser() {
    this.faxUsers.push(
      this.fb.group({
        id: '',
        customerUserId: ['', Validators.required],
        phoneNumberId: [this.editModel.id],
        allowSend: [true],
        allowReceive: [true],
        viewAllSend: [false],
        receiveEmail: [true],
      }),
    );
  }

  addAllFaxUsers() {
    const formModel = this.form.value;
    const existingUsers: string[] = formModel.faxUsers.map((user: FaxUserModel) => user.customerUserId);

    const notIncludedUsers = this.customerFaxUsers.filter(value => {
      return existingUsers.indexOf(value.id.toString()) === -1;
    });

    for (const user of notIncludedUsers) {
      this.faxUsers.push(
        this.fb.group({
          id: '',
          customerUserId: [user.id, Validators.required],
          phoneNumberId: [this.editModel.id],
          allowSend: [true],
          allowReceive: [true],
          viewAllSend: [false],
          receiveEmail: [true],
        }),
      );
    }
  }

  setFaxDomains(users: FaxDomainModel[]) {
    const domainFormGroups = users.map(item =>
      this.fb.group({
        id: [item.id],
        domain: [item.domain, Validators.required],
        phoneNumberId: [item.phoneNumberId],
        allowSend: [item.allowSend],
        allowReceive: [item.allowReceive],
        viewAllSend: [item.viewAllSend],
        receiveEmail: [item.receiveEmail],
        faxToMailSend: [item.faxToMailSend],
        faxToMailReceive: [item.faxToMailReceive],
      }),
    );
    const formArray = this.fb.array(domainFormGroups, uniqueBy('domain'));
    this.form.setControl('faxDomains', formArray);
  }

  removeFaxDomain(index) {
    this.faxDomains.removeAt(index);
  }

  addFaxDomain() {
    this.faxDomains.push(
      this.fb.group({
        id: '',
        domain: ['', Validators.required],
        phoneNumberId: [this.editModel.id],
        allowSend: [true],
        allowReceive: [true],
        viewAllSend: [false],
        receiveEmail: [true],
        faxToMailSend: [false],
        faxToMailReceive: [false],
      }),
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.refreshPhoneNumberDetails();
  }

  refreshPhoneNumberDetails(showLoading = true): void {
    this.isLoading = showLoading;
    this.loadPhoneNumberDetails()
      .then(() => {
        this.ngOnChanges();
        this.isLoading = false;
      })
      .catch(() => {});
  }

  async loadPhoneNumberDetails() {
    this.editModel = await this.phoneNumbersService.getPhoneNumberById(this.id);

    this.isInitialSmsEnabled = this.editModel.smsEnabled;

    this.phoneNumbersService.customerSmsUsersLookup().then(([users]) => {
      this.customerUsers = users;
    });

    this.phoneNumbersService.customerFaxUsersLookup().then(([users]) => {
      this.customerFaxUsers = users;
    });

    this.phoneNumbersService.customerFaxDomainsLookup().then(([domains]) => {
      this.customerFaxDomains = domains;
    });
  }

  ngOnChanges(changes?: SimpleChanges): void {
    this.form.reset({
      cnam: this.editModel.cnam,
      customerDidAlias: this.editModel.customerDidAlias,
      smsOption: this.getSmsOption(),
      smsMarkAllAsRead: this.editModel.smsMarkAllAsRead,
    });

    this.smsMessagingType = this.getSmsOption();

    if (
      this.smsMessagingType !== 'pro' &&
      this.smsMessagingType !== 'broadcasting' &&
      this.editModel.messagingUsers
      /*&& !this.editModel.allowWaba*/
    ) {
      this.editModel.messagingUsers.splice(1);
    }

    this.setMessagingUsers(this.editModel.messagingUsers);
    this.setFaxUsers(this.editModel.faxUsers);
    this.setFaxDomains(this.editModel.faxDomains);
    this.setInboundOutboundEmails(
      this.editModel.faxSettings.inboundEmails || [],
      this.editModel.faxSettings.outboundEmails || [],
    );
  }

  getSmsOption() {
    let result = 'disabled';
    if (this.editModel.smsEnabled) {
      result = 'basic';
    }
    if (this.editModel.smsEnabled && this.editModel.smsProEnabled) {
      result = 'pro';
    }
    if (this.editModel.smsEnabled && this.editModel.smsProEnabled && this.editModel.broadcastEnabled) {
      result = 'pro';
    }

    return result;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.paramsSubscription?.unsubscribe();
    this.headerService.showBackButton(false);
  }

  onMessagingTypeChanged($event: any) {
    console.log('onMessagingTypeChanged', $event);
    this.smsMessagingType = $event;
  }

  onEmailOptionsChanged(value: boolean, controlName: string, form: UntypedFormControl | AbstractControl) {
    if (controlName === 'receiveEmail' && !value) {
      form.patchValue({
        includeText: false,
      });
    }

    if (controlName === 'includeText' && value) {
      form.patchValue({
        receiveEmail: true,
      });
    }

    console.log(value, controlName);
  }

  openSupportWidget() {
    this.zendeskService.toggle();
  }
}
